import { useState } from 'react';
import { ChartBarIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { OathDualSpinner, OathEmptyState, OathAlert } from '../../shared/OathUI';
import YoYChart from './charts/YoYChart';
import PowerAmountChart from './charts/PowerAmountChart';
import AllTimeChart from './charts/AllTimeChart';

const CHART_OPTIONS = [
  { key: 'yoy', label: 'Year over Year' },
  { key: 'denomination', label: 'By Denomination' },
  { key: 'historical', label: 'Historical' },
];

/**
 * Trends Tab - compact chart view with inline selector
 */
const TrendsTab = ({ yoyChartData, denominationChartData, denominationKeys, historicalData, loading, error, onRetry }) => {
  const [selectedChart, setSelectedChart] = useState('yoy');

  const hasAnyData = yoyChartData?.length || denominationChartData?.length || historicalData?.length;

  if (loading && !hasAnyData) {
    return (
      <div className="flex flex-col items-center justify-center py-12 qhd:py-16">
        <OathDualSpinner />
        <p className="mt-3 text-xs qhd:text-sm text-gray-500 dark:text-gray-400">Loading trends...</p>
      </div>
    );
  }

  if (error && !hasAnyData) {
    return (
      <div className="py-6 qhd:py-8">
        <OathAlert variant="error">
          <div className="flex items-center justify-between gap-4">
            <span>Failed to load trends: {error}</span>
            {onRetry && (
              <button onClick={onRetry} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex-shrink-0">
                <ArrowPathIcon className="w-3.5 h-3.5" /> Retry
              </button>
            )}
          </div>
        </OathAlert>
      </div>
    );
  }

  if (!hasAnyData) {
    return (
      <div className="py-6 qhd:py-8">
        <OathEmptyState icon={ChartBarIcon} title="No Trend Data" message="No trend data is available for this agency yet." />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-700/30 rounded-2xl qhd:rounded-3xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header row: chart selector inline */}
      <div className="flex items-center justify-between px-3 py-2 qhd:px-5 qhd:py-3 4k:px-6 4k:py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="inline-flex bg-white dark:bg-gray-800 rounded-lg qhd:rounded-xl p-0.5 qhd:p-0.5 shadow-sm ring-1 ring-gray-200 dark:ring-gray-700">
          {CHART_OPTIONS.map((option) => (
            <button
              key={option.key}
              onClick={() => setSelectedChart(option.key)}
              className={`px-2.5 py-1 qhd:px-3.5 qhd:py-1.5 4k:px-4 4k:py-2 text-[10px] qhd:text-xs 4k:text-sm font-medium rounded-md qhd:rounded-lg transition-all ${
                selectedChart === option.key
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart area — tight padding */}
      <div className="p-2 qhd:p-4 4k:p-5">
        {selectedChart === 'yoy' && <YoYChart data={yoyChartData} />}
        {selectedChart === 'denomination' && <PowerAmountChart data={denominationChartData} denominationKeys={denominationKeys} />}
        {selectedChart === 'historical' && <AllTimeChart data={historicalData} />}
      </div>
    </div>
  );
};

export default TrendsTab;
